import { useEffect, useRef, useCallback } from "react";
import Swal from "sweetalert2";
import { getToken, removeToken } from "./auth";

const INACTIVITY_MS = 20 * 60 * 1000; // 20 min sin actividad → aviso
const COUNTDOWN_S = 60;               // 60 segundos para responder

export function useSessionGuard() {
    const timerRef = useRef(null);
    const countdownRef = useRef(null);
    const warningActiveRef = useRef(false);

    const logout = useCallback(async () => {
        const token = getToken();
        if (token) {
            try {
                await fetch("/api/sesion/logout", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
            } catch { /* ignorar errores de red al logout */ }
        }
        removeToken();
        Swal.close();
        window.location.href = "/";
    }, []);

    const renovar = useCallback(async () => {
        const token = getToken();
        if (!token) { logout(); return; }
        try {
            const res = await fetch("/api/sesion/renovar", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });
            if (!res.ok) logout();
        } catch {
            logout();
        }
    }, [logout]);

    const iniciarTimer = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (!getToken()) return;
            warningActiveRef.current = true;

            let segundos = COUNTDOWN_S;

            Swal.fire({
                title: "Tu sesión está por vencer",
                html: `¿Querés continuar? Cerrará en <b id="zoco-countdown">${segundos}</b>s`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Continuar",
                cancelButtonText: "Cerrar sesión",
                buttonsStyling: false,
                customClass: {
                    container: document.body.classList.contains("dark-mode")
                        ? "swal2-container--dark swal-session-warning"
                        : "swal2-container--light swal-session-warning",
                    confirmButton: "my-swal-button",
                    cancelButton: "my-swal-button2",
                },
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    countdownRef.current = setInterval(() => {
                        segundos--;
                        const el = document.getElementById("zoco-countdown");
                        if (el) el.textContent = segundos;
                        if (segundos <= 0) {
                            clearInterval(countdownRef.current);
                            logout();
                        }
                    }, 1000);
                },
                willClose: () => {
                    clearInterval(countdownRef.current);
                    warningActiveRef.current = false;
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    renovar();
                    iniciarTimer();
                } else if (result.isDismissed && result.dismiss !== Swal.DismissReason.timer) {
                    logout();
                }
            });
        }, INACTIVITY_MS);
    }, [logout, renovar]);

    const onActividad = useCallback(() => {
        if (warningActiveRef.current) return;
        iniciarTimer();
    }, [iniciarTimer]);

    useEffect(() => {
        if (!getToken()) return;

        const eventos = ["mousemove", "keydown", "click", "scroll", "touchstart"];
        eventos.forEach((e) => window.addEventListener(e, onActividad, { passive: true }));
        iniciarTimer();

        return () => {
            eventos.forEach((e) => window.removeEventListener(e, onActividad));
            clearTimeout(timerRef.current);
            clearInterval(countdownRef.current);
        };
    }, [onActividad, iniciarTimer]);
}
